import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, query, where, doc, updateDoc, deleteDoc, addDoc } from 'firebase/firestore';
import Notification from './Notification';
import Spinner from './Spinner';
import { useTracking } from '../hooks/useTracking';
import ImageGallery from './ImageGallery';
import ProductForm from './ProductForm';
import { useEditMode } from '../contexts/EditModeContext';
import useGlobalTheme from '../hooks/useGlobalTheme';
import './Products.css';
import './QuantityStepper.css';

const formatNumber = (number) => {
  if (typeof number !== 'number' || isNaN(number)) {
    return '0,00';
  }
  return number
    .toFixed(2)
    .replace('.', ',')
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const CategoryProducts = ({ cart, setCart }) => {
  const { categoryName } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ message: '', show: false });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showCategories, setShowCategories] = useState(false);
  const categoryDropdownRef = useRef(null);
  const { isDarkMode } = useGlobalTheme();

  const { isEditMode, isAdmin } = useEditMode();
  const { trackAddToCart, trackViewItem } = useTracking();

  const showNotification = (message, type = 'success') => {
    setNotification({ message, show: true, type });
    setTimeout(() => {
      setNotification({ message: '', show: false });
    }, 3000);
  };

  const fetchProductsByCategory = useCallback(async () => {
    const decoded = decodeURIComponent(categoryName);
    try {
      const [byCategorySnap, byCategoriesSnap] = await Promise.all([
        getDocs(query(collection(db, 'products'), where('category', '==', decoded))),
        getDocs(query(collection(db, 'products'), where('categories', 'array-contains', decoded)))
      ]);
      const byId = new Map();
      byCategorySnap.docs.forEach(d => byId.set(d.id, { ...d.data(), id: d.id }));
      byCategoriesSnap.docs.forEach(d => byId.set(d.id, { ...d.data(), id: d.id }));
      setProducts(Array.from(byId.values()));
    } catch (error) {
      console.error("Error fetching products by category:", error);
      showNotification('Error al cargar productos de la categoría.', 'error');
    } finally {
      setLoading(false);
    }
  }, [categoryName]);

  useEffect(() => {
    fetchProductsByCategory();
  }, [fetchProductsByCategory]);

  useEffect(() => {
    if (!showCategories) return;
    const handlePointerDown = (e) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target)) {
        setShowCategories(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [showCategories]);

  // Cargar todas las categorías para el formulario de edición (category + categories)
  useEffect(() => {
    const fetchAllCategories = async () => {
      try {
        const allProductsSnap = await getDocs(collection(db, 'products'));
        const allProducts = allProductsSnap.docs.map(d => d.data());
        const fromCategory = allProducts.map(p => p.category).filter(Boolean);
        const fromCategories = allProducts.flatMap(p => Array.isArray(p.categories) ? p.categories : []);
        const uniqueCategories = [...new Set([...fromCategory, ...fromCategories])];
        setCategories(uniqueCategories);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchAllCategories();
  }, []);

  const addToCart = (product, quantity) => {
    const existingProduct = cart.find(item => item.id === product.id);
    const existingQuantity = existingProduct ? existingProduct.quantity : 0;
    const noStockCheck = product.excludeFromStock === true;

    if (!noStockCheck && product.stock < quantity + existingQuantity) {
      showNotification('No hay suficiente stock disponible.', 'error');
      return;
    }

    const updatedCart = existingProduct
      ? cart.map(item => item.id === product.id ? { ...item, quantity: item.quantity + quantity } : item)
      : [...cart, { ...product, quantity }];

    setCart(updatedCart);
    showNotification(`Añadido ${quantity} ${product.model} al carrito.`, 'success');
    trackAddToCart({
      id: product.id,
      name: product.model,
      price: product.price,
      quantity
    });
  };

  const handleProductClick = (product) => {
    navigate(`/producto/${product.id}`);
    trackViewItem({
      id: product.id,
      name: product.model,
      price: product.price,
      quantity: 1
    });
  };

  const handleAction = async (action, product) => {
    if (!isEditMode && (action === 'edit' || action === 'delete' || action === 'duplicate')) {
      navigate('/login');
      return;
    }
    if (action === 'edit') {
      setEditProduct(product);
      setIsModalOpen(true);
    } else if (action === 'delete') {
      const confirmDelete = window.confirm('¿Estás seguro de que deseas eliminar este producto?');
      if (confirmDelete) {
        try {
          await deleteDoc(doc(db, 'products', product.id));
          setProducts(prev => prev.filter(p => p.id !== product.id));
          showNotification('Producto eliminado con éxito.', 'success');
        } catch (error) {
          console.error('Error al eliminar el producto: ', error);
          showNotification('Error al eliminar el producto.', 'error');
        }
      }
    } else if (action === 'duplicate') {
      try {
        const newProductData = { ...product, model: `${product.model} (copy)` };
        delete newProductData.id;
        newProductData.images = [...(product.images || [])];
        const docRef = await addDoc(collection(db, 'products'), newProductData);
        setProducts(prev => [...prev, { ...newProductData, id: docRef.id }]);
        showNotification('Producto duplicado con éxito.', 'success');
      } catch (error) {
        console.error('Error duplicating product: ', error);
        showNotification('Error al duplicar el producto.', 'error');
      }
    }
  };

  const handleProductSubmit = async (productData) => {
    setLoading(true);
    try {
      if (editProduct) {
        await updateDoc(doc(db, 'products', editProduct.id), productData);
        setProducts(prev => prev.map(p => p.id === editProduct.id ? { ...productData, id: editProduct.id } : p));
        showNotification('Producto actualizado con éxito.', 'success');
      }
      setIsModalOpen(false);
      setEditProduct(null);
      // Refrescar productos de la categoría por si cambió
      await fetchProductsByCategory();
    } catch (error) {
      console.error('Error al guardar el producto: ', error);
      showNotification('Error al guardar el producto.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products
    .filter(product => !product.hidden || isAdmin)
    .sort((a, b) => a.model.localeCompare(b.model));

  if (loading) return <Spinner />;

  return (
    <div className={`products-container ${isDarkMode ? 'dark' : 'light'}`}>
      {notification.show && (
        <Notification 
          message={notification.message} 
          show={notification.show} 
          type={notification.type} 
        />
      )}
      
      <div className="products-header">
        <button onClick={() => navigate('/productos')} className="back-button">
          ← Volver a Productos
        </button>
        <h1 className="products-title title-gradient-lm">Categoría: {decodeURIComponent(categoryName)}</h1>
      </div>

      {/* Selector de otras categorías (estilo similar a Products) */}
      <div className="category-buttons-container">
        <div className="dropdown-container" ref={categoryDropdownRef}>
          <button type="button" className="category-button" onClick={() => setShowCategories(!showCategories)}>
            VER POR CATEGORÍAS
          </button>
          {showCategories && (
            <div className="dropdown-content-container show">
              {categories
                .sort((a, b) => a.localeCompare(b))
                .map(category => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => {
                      setShowCategories(false);
                      navigate(`/categoria/${encodeURIComponent(category)}`);
                    }}
                    className="dropdown-content-button"
                  >
                    {category}
                  </button>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* category-description removed as requested */}

      <div className="ball-list-container">
        {filteredProducts.length > 0 ? (
          filteredProducts.map((product) => (
            <ProductItem
              key={product.id}
              product={product}
              addToCart={addToCart}
              onProductClick={handleProductClick}
              isAdmin={isAdmin}
              isEditMode={isEditMode}
              handleAction={handleAction}
            />
          ))
        ) : (
          <div className="no-products">
            <h3>No hay productos en esta categoría</h3>
            <button onClick={() => navigate('/productos')} className="back-button">
              Ver todos los productos
            </button>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="modal-overlay-container">
          <div className="modal-content-container">
            <ProductForm
              product={editProduct}
              onSubmit={handleProductSubmit}
              onCancel={() => {
                setIsModalOpen(false);
                setEditProduct(null);
              }}
              categories={categories}
              setCategories={setCategories}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const MIN_PRODUCT_QTY = 1;

const ProductItem = ({ product, addToCart, onProductClick, isAdmin, isEditMode, handleAction }) => {
  const [quantity, setQuantity] = useState(1);
  const available = product.excludeFromStock === true || product.stock > 0;
  const maxQty = product.excludeFromStock === true ? 999 : product.stock;
  const upperBound = maxQty < MIN_PRODUCT_QTY ? MIN_PRODUCT_QTY : maxQty;

  const handleQuantityChange = (e) => {
    const raw = e.target.value;
    if (raw === '') return;
    const num = parseInt(raw, 10);
    if (Number.isNaN(num)) return;
    const clamped = Math.min(upperBound, Math.max(MIN_PRODUCT_QTY, num));
    setQuantity(clamped);
  };

  const handleQuantityBlur = (e) => {
    const raw = e.target.value.trim();
    if (raw === '') {
      setQuantity(MIN_PRODUCT_QTY);
      return;
    }
    const num = parseInt(raw, 10);
    if (Number.isNaN(num) || num < MIN_PRODUCT_QTY) {
      setQuantity(MIN_PRODUCT_QTY);
    } else if (num > upperBound) {
      setQuantity(upperBound);
    }
  };

  const handleQuantityStep = (delta) => {
    setQuantity((prev) => {
      const next = prev + delta;
      if (next >= MIN_PRODUCT_QTY && next <= upperBound) return next;
      return prev;
    });
  };

  return (
    <div className={`product-item-container ${!available ? 'out-of-stock' : ''} ${product.hidden ? 'hidden' : ''}`}>
      <BallCard
        model={product.model}
        images={product.images || []}
        imagePositions={product.imagePositions}
        price={`$${formatNumber(product.price)}`}
        category={product.category}
        onClick={() => onProductClick(product)}
      />
      <div className="product-actions-container">
        <div className="product-quantity-row">
          <label htmlFor={`quantity-${product.id}`} className="product-quantity-label">Cantidad</label>
          <div className="quantity-stepper">
            <button
              type="button"
              className="quantity-stepper-btn quantity-stepper-btn--minus"
              onClick={() => handleQuantityStep(-1)}
              disabled={!available || quantity <= MIN_PRODUCT_QTY}
              aria-label="Reducir cantidad"
            >
              −
            </button>
            <div className="quantity-stepper-field">
              <input
                type="number"
                id={`quantity-${product.id}`}
                value={quantity}
                min={MIN_PRODUCT_QTY}
                max={upperBound}
                onChange={handleQuantityChange}
                onBlur={handleQuantityBlur}
                className="quantity-input"
                aria-label="Cantidad"
                inputMode="numeric"
                pattern="[0-9]*"
                disabled={!available}
              />
            </div>
            <button
              type="button"
              className="quantity-stepper-btn quantity-stepper-btn--plus"
              onClick={() => handleQuantityStep(1)}
              disabled={!available || quantity >= upperBound}
              aria-label="Aumentar cantidad"
            >
              +
            </button>
          </div>
        </div>
        <button 
          className="product-action-button add" 
          onClick={() => addToCart(product, quantity)} 
          disabled={!available}
        >
          {available ? 'Agregar al Carrito' : 'Sin Stock'}
        </button>
        {isEditMode && (
          <>
            <button className="product-action-button" onClick={() => handleAction('edit', product)}>
              Editar
            </button>
            <button className="product-action-button delete" onClick={() => handleAction('delete', product)}>
              Eliminar
            </button>
            <button className="product-action-button duplicate" onClick={() => handleAction('duplicate', product)}>
              Duplicar Producto
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const BallCard = ({ model, images, imagePositions, price, onClick }) => { 
  return (
    <div className="ball-card-container" onClick={onClick}> 
      <ImageGallery
        images={images}
        alt={model}
        imagePositions={imagePositions}
        className="ball-card-gallery"
        cycleImagesOnHover
      />
      <h3 className="ball-card-title">{model}</h3>
      <p className="ball-card-price">{price}</p>
    </div>
  );
};

export default CategoryProducts; 